import { ID, LinkOptions, SectionContainer, Task } from '@griply/types';

import { BatchDocuments, WriteBatchFn } from '../batch-documents';
import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';

export type TransferSectionOptions<
  TDocRef = unknown,
  TCollectionRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  sectionId: ID;
  from: SectionContainer;
  fromType: LinkOptions;
  to: SectionContainer;
  toType: LinkOptions;
  // The section's tasks, resolved by the caller (each app fetches with its SDK).
  tasks: Task[];
  collection: (
    db: NoInfer<TDb>,
    path: string,
    ...rest: string[]
  ) => TCollectionRef;
  doc: (collectionRef: NoInfer<TCollectionRef>, id: string) => TDocRef;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  writeBatch: WriteBatchFn<TDocRef, TDb>;
  onWriteError: (error: unknown) => void;
};

// Moves a section (and its tasks) between any two containers — goal→goal,
// goal→life-area, life-area→goal, life-area→life-area. Each task is re-homed to
// the destination: moving to a goal sets `goalId` and clears `lifeAreaId`;
// moving to a life area sets `lifeAreaId` and clears `goalId`. Goal containers
// also get their task counts adjusted (life areas don't track counts).
export const transferSection = async <
  TDocRef,
  TCollectionRef,
  TFieldValue,
  TDb = FirestoreInstance,
>(
  options: TransferSectionOptions<TDocRef, TCollectionRef, TFieldValue, TDb>,
): Promise<void> => {
  const {
    sectionId,
    from,
    fromType,
    to,
    toType,
    tasks,
    collection,
    doc,
    db,
    serverTimestamp,
    writeBatch,
    onWriteError,
  } = options;

  const section = from.sections?.find(({ id }) => id === sectionId);
  if (!section) {
    return;
  }

  // Moving a section onto its own container is a no-op — and would race two
  // `sections` writes on the same doc within the batch.
  if (from.id === to.id && fromType === toType) {
    return;
  }

  const batchDocuments = new BatchDocuments<TDocRef, TDb>(db, writeBatch);
  const goalCollection = collection(db, FirestoreCollections.Goals);
  const lifeAreaCollection = collection(db, FirestoreCollections.LifeAreas);
  const taskCollection = collection(db, FirestoreCollections.Tasks);

  const collectionRefFor = (type: LinkOptions): TCollectionRef =>
    type === LinkOptions.Goal ? goalCollection : lifeAreaCollection;

  const completedTasks = tasks.filter(({ completedAt }) => !!completedAt);

  // Remove the section from the source container.
  batchDocuments.updateDocument({
    ref: doc(collectionRefFor(fromType), from.id),
    data: {
      sections: from.sections?.filter(({ id }) => id !== sectionId) ?? null,
      ...(fromType === LinkOptions.Goal && {
        taskCount: Math.max((from.taskCount ?? 0) - tasks.length, 0),
        completedTaskCount: Math.max(
          (from.completedTaskCount ?? 0) - completedTasks.length,
          0,
        ),
      }),
      updatedAt: serverTimestamp(),
    },
  });

  // Append the section to the target container.
  batchDocuments.updateDocument({
    ref: doc(collectionRefFor(toType), to.id),
    data: {
      sections: [...(to.sections ?? []), section],
      ...(toType === LinkOptions.Goal && {
        taskCount: Math.max((to.taskCount ?? 0) + tasks.length, 0),
        completedTaskCount: Math.max(
          (to.completedTaskCount ?? 0) + completedTasks.length,
          0,
        ),
      }),
      updatedAt: serverTimestamp(),
    },
  });

  // Re-home every section task to the destination container.
  const taskLink =
    toType === LinkOptions.Goal
      ? { goalIds: [to.id], lifeAreaIds: [], goalId: to.id, lifeAreaId: null }
      : { goalIds: [], lifeAreaIds: [to.id], goalId: null, lifeAreaId: to.id };
  tasks.forEach((task) => {
    batchDocuments.updateDocument({
      ref: doc(taskCollection, task.id),
      data: { ...taskLink, updatedAt: serverTimestamp() },
    });
  });

  detachWrite(batchDocuments.commit(), onWriteError);
};
